import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Box,
    Grid,
    Paper,
    Typography,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableRow,
    List,
    ListItem,
    ListItemText,
    Divider,
    Button,
    Alert,
    Tabs,
    Tab,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    alpha,
} from '@mui/material';
import {
    CheckCircle as HealthyIcon,
    Error as UnhealthyIcon,
    People as UsersIcon,
    Notifications as AlertsIcon,
    EmojiEvents as TrophyIcon,
    Warning as WarningIcon,
    OpenInNew as OpenInNewIcon,
    FactCheck as FactCheckIcon,
    EventAvailable as EventAvailableIcon,
    WarningAmber as WarningAmberIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Groups as GroupsIcon,
} from '@mui/icons-material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
} from 'recharts';
import { useSystemHealthQuery, useAllUsersQuery, useRecentAuditLogsQuery, usePoplachStatusQuery } from '../../api/JsdhNotifikaceService/service/AdminMutations';
import { useTechnikaKontrolyOverviewQuery } from '../../api/JsdhNotifikaceService/service/TechnikaMutations';
import { useKontrolyOverviewQuery } from '../../api/JsdhNotifikaceService/service/KontrolyMutations';
import { useDostupnostJednotkyQuery, useHzsSmenaAktualniQuery } from '../../api/JsdhNotifikaceService/service/DostupnostMutations';
import { usePohotovostAktivniQuery, usePohotovostOdpovediQuery } from '../../api/JsdhNotifikaceService/service/PohotovostMutations';
import type { Pohotovost } from '../../api/JsdhNotifikaceService/generated/models/Pohotovost';
import { usePredpoplachAktivniQuery, usePredpoplachOdpovediQuery, useDeaktivujPredpoplachMutation } from '../../api/JsdhNotifikaceService/service/PredpoplachMutations';
import type { KontrolaStatus, KontrolaStatusResponse } from '../../api/JsdhNotifikaceService/generated/models/Kontrola';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import JsdhNotifikaceService from '../../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import type { DashboardStats, Udalost, Odpoved, Konfigurace } from '../../api/JsdhNotifikaceService/generated';
import { formatujDobuReakce } from '../../utils/statistikyUtils';
import LiveTimeline from '../../components/LiveTimeline';
import { PageHeader, KontrolaDialog, EmptyState } from '../../components/admin';

// Překlad statusů do češtiny
const translateStatus = (status: string): string => {
    switch (status.toUpperCase()) {
        case 'UP':
            return 'V pořádku';
        case 'DOWN':
            return 'Nefunkční';
        case 'DEGRADED':
            return 'Zhoršený';
        case 'UNKNOWN':
            return 'Neznámý';
        case 'SUCCESS':
            return 'Úspěch';
        case 'FAILURE':
        case 'ERROR':
            return 'Chyba';
        default:
            return status;
    }
};

// Překlad typů poplachů
const translateTyp = (typ: string): string => {
    const typy: Record<string, string> = {
        'POZAR': 'Požár',
        'TECHNICKA_POMOC': 'Technická pomoc',
        'DOPRAVNI_NEHODA': 'Dopravní nehoda',
        'UNIK_LATEK': 'Únik látek',
        'JINE': 'Jiné',
    };
    return typy[typ] || typ;
};

// HO-36: rozšířeno o 3 nové kategorie (BOURKA/TEPLOTNI_EXTREM/DEST)
const kategorieLabels: Record<string, string> = {
    VITR: 'Vítr',
    POVODNE: 'Povodně',
    SNEHOVA_KALAMITA: 'Sněhová kalamita',
    POZARNI_NEBEZPECI: 'Požární nebezpečí',
    BOURKA: 'Bouřka',
    TEPLOTNI_EXTREM: 'Teplotní extrém',
    DEST: 'Vydatný déšť',
    JINE: 'Jiné',
};

// Barvy pro koláčový graf
const PIE_COLORS = ['#ef5350', '#42a5f5', '#66bb6a', '#ffa726', '#ab47bc', '#26a69a'];

// Překlad dne v týdnu
const formatDayLabel = (datum: string): string => {
    const date = new Date(datum);
    const dny = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];
    return dny[date.getDay()];
};

const formatDateTime = (value: string): string => {
    return new Date(value).toLocaleString('cs-CZ', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

// Sub-komponenta pro pohotovost na dashboardu (volá hook pro odpovědi)
const PohotovostDashboardItem: React.FC<{ pohotovost: Pohotovost }> = ({ pohotovost }) => {
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const { data: odpovedi } = usePohotovostOdpovediQuery(pohotovost.id);
    const dostupnych = odpovedi?.filter(o => o.odpoved === 'DOSTUPNY').length ?? 0;
    const nedostupnych = odpovedi?.filter(o => o.odpoved === 'NEDOSTUPNY').length ?? 0;

    return (
        <>
            <Box
                sx={{ mb: 1.5, '&:last-child': { mb: 0 }, cursor: 'pointer', borderRadius: 1, p: 1, mx: -1, '&:hover': { backgroundColor: 'action.hover' } }}
                onClick={() => setDialogOpen(true)}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                        {pohotovost.nazev}
                    </Typography>
                    {pohotovost.kategorie && (
                        <Chip label={kategorieLabels[pohotovost.kategorie] || pohotovost.kategorie} size="small" />
                    )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                    Od: {formatDateTime(pohotovost.platnostOd)}
                    {pohotovost.vytvorilJmeno && ` · ${pohotovost.vytvorilJmeno}`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip
                        icon={<CheckCircleIcon />}
                        label={`${dostupnych} dostupných`}
                        color="success"
                        size="small"
                        variant="outlined"
                    />
                    <Chip
                        icon={<CancelIcon />}
                        label={`${nedostupnych} nedostupných`}
                        color="error"
                        size="small"
                        variant="outlined"
                    />
                </Box>
            </Box>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Odpovědi na výzvu</DialogTitle>
                <DialogContent>
                    {odpovedi && odpovedi.length > 0 ? (
                        <Box>
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Chip icon={<CheckCircleIcon />} label={`${dostupnych} dostupných`} color="success" size="small" />
                                    <Chip icon={<CancelIcon />} label={`${nedostupnych} nedostupných`} color="error" size="small" />
                                </Box>
                            </Box>
                            <Divider sx={{ mb: 1 }} />
                            {[...odpovedi]
                                .sort((a, b) => (a.odpoved === 'DOSTUPNY' ? -1 : 1) - (b.odpoved === 'DOSTUPNY' ? -1 : 1))
                                .map(o => (
                                    <Box
                                        key={o.id}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 1,
                                            px: 1,
                                            borderRadius: 1,
                                            '&:hover': { backgroundColor: 'action.hover' },
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="body2" fontWeight={500}>
                                                {o.jmeno || `Uživatel #${o.uzivatelId}`}
                                            </Typography>
                                            {o.funkce && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {typeof o.funkce === 'string'
                                                        ? o.funkce
                                                        : Array.isArray(o.funkce)
                                                            ? (o.funkce as any[]).map(f => typeof f === 'string' ? f : f?.kod || '').join(', ')
                                                            : typeof o.funkce === 'object' && o.funkce !== null
                                                                ? (o.funkce as any).kod || ''
                                                                : String(o.funkce)}
                                                </Typography>
                                            )}
                                            {o.poznamka && (
                                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>
                                                    „{o.poznamka}"
                                                </Typography>
                                            )}
                                            {o.cas && (
                                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                                                    {formatDateTime(o.cas)}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Chip
                                            icon={o.odpoved === 'DOSTUPNY' ? <CheckCircleIcon /> : <CancelIcon />}
                                            label={o.odpoved === 'DOSTUPNY' ? 'Dostupný' : 'Nedostupný'}
                                            color={o.odpoved === 'DOSTUPNY' ? 'success' : 'error'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                ))}
                        </Box>
                    ) : (
                        <EmptyState
                            icon={<GroupsIcon />}
                            title="Zatím žádné odpovědi"
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Zavřít</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const AdminDashboardPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { data: health, isLoading: healthLoading } = useSystemHealthQuery();
    const { isLoading: usersLoading } = useAllUsersQuery();
    const { data: auditLogs, isLoading: auditLoading } = useRecentAuditLogsQuery(10);
    const { data: dashboardStats, isLoading: statsLoading } = useQuery<DashboardStats>({
        queryKey: ['dashboardStats'],
        queryFn: () => JsdhNotifikaceService.getDashboardStats(),
    });
    const { data: poplachStatus } = usePoplachStatusQuery();
    const { data: dostupnost } = useDostupnostJednotkyQuery();
    const { data: hzsSmenaData } = useHzsSmenaAktualniQuery();
    const currentShift = hzsSmenaData?.currentShift || null;
    const { data: aktivniPohotovosti } = usePohotovostAktivniQuery();
    const { data: aktivniPredpoplach } = usePredpoplachAktivniQuery();
    const { data: predpoplachOdpovedi } = usePredpoplachOdpovediQuery(aktivniPredpoplach?.id ?? null);
    const deaktivujPredpoplachMutation = useDeaktivujPredpoplachMutation();
    const [predpoplachDeactivateDialogOpen, setPredpoplachDeactivateDialogOpen] = React.useState(false);
    const [expandedDostupnost, setExpandedDostupnost] = React.useState<'dostupni' | 'omezene' | 'nedostupni' | 'naSmene' | null>(null);
    const dostupnostDefaultSet = React.useRef(false);
    const { data: kontrolyOverview } = useKontrolyOverviewQuery();
    const [kontrolaDialogState, setKontrolaDialogState] = React.useState<{
        open: boolean;
        uzivatelId: number;
        uzivatelJmeno: string;
        kontrolaStatus: KontrolaStatusResponse;
    }>({ open: false, uzivatelId: 0, uzivatelJmeno: '', kontrolaStatus: {} as KontrolaStatusResponse });

    // Countdown pro předpoplach
    const [predpoplachNow, setPredpoplachNow] = React.useState(Date.now());
    React.useEffect(() => {
        if (!aktivniPredpoplach) return;
        const interval = setInterval(() => setPredpoplachNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, [aktivniPredpoplach]);
    const predpoplachCountdown = React.useMemo(() => {
        if (!aktivniPredpoplach?.casExpirace) return "--:--";
        const diff = new Date(aktivniPredpoplach.casExpirace).getTime() - predpoplachNow;
        if (diff <= 0) return "00:00";
        const m = Math.floor(diff / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    }, [aktivniPredpoplach?.casExpirace, predpoplachNow]);
    const predpoplachStats = React.useMemo(() => {
        if (!predpoplachOdpovedi) return { jedu: 0, nejedu: 0 };
        return {
            jedu: predpoplachOdpovedi.filter(o => o.odpoved === "JEDU" && !o.automaticka).length,
            nejedu: predpoplachOdpovedi.filter(o => o.odpoved === "NEJEDU").length,
        };
    }, [predpoplachOdpovedi]);
    const { data: technikaKontrolyOverview } = useTechnikaKontrolyOverviewQuery();
    const { data: konfigurace } = useQuery<Konfigurace[]>({
        queryKey: ['konfigurace'],
        queryFn: () => JsdhNotifikaceService.getAllKonfigurace(),
        staleTime: 5 * 60 * 1000,
    });

    const dostupnostStats = React.useMemo(() => {
        if (!dostupnost) return null;
        return {
            dostupni: dostupnost.filter(d => d.stav === 'DOSTUPNY'),
            omezene: dostupnost.filter(d => d.stav === 'OMEZENE_DOSTUPNY'),
            nedostupni: dostupnost.filter(d => d.stav === 'NEDOSTUPNY'),
            naSmene: currentShift ? dostupnost.filter(d => d.hzsSmena === currentShift) : [],
        };
    }, [dostupnost, currentShift]);

    React.useEffect(() => {
        if (dostupnostStats && !dostupnostDefaultSet.current) {
            dostupnostDefaultSet.current = true;
            if (dostupnostStats.dostupni.length > 0) {
                setExpandedDostupnost('dostupni');
            } else if (dostupnostStats.omezene.length > 0) {
                setExpandedDostupnost('omezene');
            } else if (dostupnostStats.nedostupni.length > 0) {
                setExpandedDostupnost('nedostupni');
            }
        }
    }, [dostupnostStats]);

    const dashboardKontrolyEnabled = konfigurace?.find(k => k.klic === 'dashboard_upozorneni_kontroly')?.hodnota !== '0';
    const dashboardTechnikaKontrolyEnabled = konfigurace?.find(k => k.klic === 'dashboard_upozorneni_technika_kontroly')?.hodnota !== '0';

    const kontrolyWarnings = React.useMemo(() => {
        if (!kontrolyOverview || !dashboardKontrolyEnabled) return [];
        const problems: { uzivatelId: number; uzivatelJmeno: string; typNazev: string; status: KontrolaStatus; datumExpirace?: string; kontrolaStatus: KontrolaStatusResponse }[] = [];
        for (const row of kontrolyOverview.uzivatele) {
            for (const k of row.kontroly) {
                if (k.relevantni && k.zobrazitDashboard && (k.status === 'EXPIROVANA' || k.status === 'BLIZI_SE_EXPIRACE')) {
                    problems.push({
                        uzivatelId: row.uzivatelId,
                        uzivatelJmeno: row.jmeno,
                        typNazev: k.typKontrolyNazev,
                        status: k.status,
                        datumExpirace: k.datumExpirace,
                        kontrolaStatus: k,
                    });
                }
            }
        }
        return problems;
    }, [kontrolyOverview, dashboardKontrolyEnabled]);

    const kontrolyMissing = React.useMemo(() => {
        if (!kontrolyOverview || !dashboardKontrolyEnabled) return 0;
        let count = 0;
        for (const row of kontrolyOverview.uzivatele) {
            for (const k of row.kontroly) {
                if (k.relevantni && k.zobrazitDashboard && k.status === 'CHYBI') {
                    count++;
                }
            }
        }
        return count;
    }, [kontrolyOverview, dashboardKontrolyEnabled]);

    const kontrolyExpired = kontrolyWarnings.filter(k => k.status === 'EXPIROVANA');
    const kontrolyApproaching = kontrolyWarnings.filter(k => k.status === 'BLIZI_SE_EXPIRACE');

    const technikaKontrolyWarnings = React.useMemo(() => {
        if (!technikaKontrolyOverview || !dashboardTechnikaKontrolyEnabled) return [];
        const problems: { volaciZnak: string; typNazev: string; status: KontrolaStatus; datumExpirace?: string }[] = [];
        for (const row of technikaKontrolyOverview.vozidla) {
            for (const k of row.kontroly) {
                if (k.zobrazitDashboard && (k.status === 'EXPIROVANA' || k.status === 'BLIZI_SE_EXPIRACE')) {
                    problems.push({
                        volaciZnak: row.volaciZnak,
                        typNazev: k.typKontrolyNazev,
                        status: k.status,
                        datumExpirace: k.datumExpirace,
                    });
                }
            }
        }
        return problems;
    }, [technikaKontrolyOverview, dashboardTechnikaKontrolyEnabled]);

    const technikaKontrolyExpired = technikaKontrolyWarnings.filter(k => k.status === 'EXPIROVANA');
    const technikaKontrolyApproaching = technikaKontrolyWarnings.filter(k => k.status === 'BLIZI_SE_EXPIRACE');

    const hasKontrolyProblems = kontrolyWarnings.length > 0 || kontrolyMissing > 0 || technikaKontrolyWarnings.length > 0;

    // Query pro aktivní poplach
    const { data: aktivniPoplach } = useQuery<Udalost>({
        queryKey: ['aktivniPoplach'],
        queryFn: () => JsdhNotifikaceService.getAktivniUdalost(),
        refetchInterval: 10000,
    });

    // Query pro odpovědi (pouze pokud je aktivní poplach)
    const { data: odpovedi = [] } = useQuery<Odpoved[]>({
        queryKey: ['odpovedi', aktivniPoplach?.id],
        queryFn: () => JsdhNotifikaceService.getOdpovediByPoplachId(aktivniPoplach!.id),
        enabled: Boolean(aktivniPoplach?.id),
        refetchInterval: 4000,
    });

    const isLoading = healthLoading || usersLoading || auditLoading || statsLoading;

    // Příprava dat pro koláčový graf
    const pieChartData = React.useMemo(() => {
        if (!dashboardStats?.poplachyPodleTypu) return [];
        return Object.entries(dashboardStats.poplachyPodleTypu).map(([typ, pocet]) => ({
            name: translateTyp(typ),
            value: pocet,
        }));
    }, [dashboardStats?.poplachyPodleTypu]);

    // Příprava dat pro sloupcový graf
    const barChartData = React.useMemo(() => {
        if (!dashboardStats?.posledni7Dni) return [];
        return dashboardStats.posledni7Dni.map((day) => ({
            den: formatDayLabel(day.datum),
            poplachy: day.pocetPoplachu,
            odpovedi: day.pocetOdpovedi,
        }));
    }, [dashboardStats?.posledni7Dni]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    // Sdílená komponenta pro System Health Paper
    const SystemHealthSection = (
        <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
                Stav systému
            </Typography>
            {health && (
                <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        {health.overallStatus === 'UP' ? (
                            <HealthyIcon color="success" />
                        ) : (
                            <UnhealthyIcon color="error" />
                        )}
                        <Chip
                            label={translateStatus(health.overallStatus)}
                            color={health.overallStatus === 'UP' ? 'success' : 'error'}
                            size="small"
                        />
                    </Box>
                    <List dense>
                        {health.components?.map((component, index) => (
                            <ListItem key={index}>
                                <ListItemText
                                    primary={component.name}
                                    secondary={component.message}
                                />
                                <Chip
                                    label={translateStatus(component.status)}
                                    color={component.status === 'UP' ? 'success' : 'error'}
                                    size="small"
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}
        </Paper>
    );

    return (
        <Box sx={{ overflow: 'hidden' }}>
            <PageHeader title="Dashboard" />

            {/* Poplach Status Banner */}
            {poplachStatus && (
                <Paper
                    sx={{
                        p: 2.5,
                        mb: 3,
                        bgcolor: poplachStatus.alert ? 'error.main' : 'success.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        {poplachStatus.alert ? (
                            <WarningIcon sx={{ fontSize: 32 }} />
                        ) : (
                            <HealthyIcon sx={{ fontSize: 32 }} />
                        )}
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                {poplachStatus.alert ? 'AKTIVNÍ POPLACH' : 'Žádný aktivní poplach'}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {poplachStatus.alert
                                    ? 'V systému je právě aktivní poplach'
                                    : 'Aktuálně nejsou žádné aktivní události'}
                            </Typography>
                        </Box>
                    </Box>
                    {poplachStatus.alert && (
                        <Button
                            component={RouterLink}
                            to="/poplach"
                            variant="contained"
                            endIcon={<OpenInNewIcon />}
                            sx={{
                                bgcolor: 'white',
                                color: 'error.main',
                                '&:hover': { bgcolor: 'grey.100' },
                            }}
                        >
                            Zobrazit poplach
                        </Button>
                    )}
                </Paper>
            )}

            {/* Předpoplach Banner */}
            {aktivniPredpoplach && (
                <Paper
                    sx={{
                        p: 2.5,
                        mb: 3,
                        bgcolor: '#FF8F00',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <WarningAmberIcon sx={{ fontSize: 32 }} />
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                PŘEDPOPLACH AKTIVNÍ — {aktivniPredpoplach.nazev}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {predpoplachStats.jedu} JEDU / {predpoplachStats.nejedu} NEJEDU · zbývá {predpoplachCountdown}
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={() => setPredpoplachDeactivateDialogOpen(true)}
                        sx={{
                            bgcolor: 'white',
                            color: '#FF8F00',
                            '&:hover': { bgcolor: 'grey.100' },
                        }}
                    >
                        Deaktivovat
                    </Button>
                </Paper>
            )}

            {/* Live Timeline - zobrazit pouze pokud je aktivní poplach */}
            {aktivniPoplach && (
                <Box sx={{ mb: 3 }}>
                    <LiveTimeline
                        odpovedi={odpovedi}
                        casVyhlaseni={aktivniPoplach.casVyhlaseni}
                    />
                </Box>
            )}

            {/* Statistiky za tento měsíc */}
            {dashboardStats?.mesic && (
                <Paper sx={{ p: 2.5, mb: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                        <AlertsIcon color="primary" />
                        <Typography variant="h6" fontWeight={600}>Tento měsíc</Typography>
                    </Box>
                    <Grid container spacing={2.5}>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography variant="caption" color="text.secondary">Poplachů</Typography>
                            <Typography variant="h4" fontWeight={700}>{dashboardStats.mesic.pocetPoplachu}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography variant="caption" color="text.secondary">Odpovědí</Typography>
                            <Typography variant="h4" fontWeight={700}>{dashboardStats.mesic.pocetOdpovedi}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography variant="caption" color="text.secondary">JEDU / NEJEDU</Typography>
                            <Box display="flex" gap={0.5} alignItems="baseline">
                                <Typography variant="h4" fontWeight={700} color="success.main">
                                    {dashboardStats.mesic.pocetJedu}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">/</Typography>
                                <Typography variant="h4" fontWeight={700} color="error.main">
                                    {dashboardStats.mesic.pocetNejedu}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <Typography variant="caption" color="text.secondary">Ø doba reakce</Typography>
                            <Typography variant="h4" fontWeight={700}>
                                {formatujDobuReakce(dashboardStats.mesic.prumernaDobaReakce)}
                            </Typography>
                        </Grid>
                    </Grid>
                    {/* Nejaktivnější + Nejrychlejší členové */}
                    {((dashboardStats.nejaktivnejsiClenove && dashboardStats.nejaktivnejsiClenove.length > 0) ||
                      (dashboardStats.nejrychlejsiClenove && dashboardStats.nejrychlejsiClenove.length > 0)) && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2.5}>
                                {dashboardStats.nejaktivnejsiClenove && dashboardStats.nejaktivnejsiClenove.length > 0 && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                                            <UsersIcon color="primary" />
                                            <Typography variant="h6" fontWeight={600}>
                                                Nejaktivnější členové
                                            </Typography>
                                        </Box>
                                        <List dense>
                                            {dashboardStats.nejaktivnejsiClenove.map((member, index) => (
                                                <ListItem key={index}>
                                                    <ListItemText
                                                        primary={member.jmeno}
                                                        secondary={`${member.pocetOdpovedi} odpovědí • Ø ${formatujDobuReakce(member.prumernaDobaReakce)}`}
                                                    />
                                                    {member.funkce && (
                                                        <Chip label={member.funkce} size="small" variant="outlined" />
                                                    )}
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Grid>
                                )}
                                {dashboardStats.nejrychlejsiClenove && dashboardStats.nejrychlejsiClenove.length > 0 && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                                            <TrophyIcon sx={{ color: '#FFD700' }} />
                                            <Typography variant="h6" fontWeight={600}>
                                                Nejrychlejší členové
                                            </Typography>
                                        </Box>
                                        <List dense>
                                            {dashboardStats.nejrychlejsiClenove.map((member, index) => (
                                                <ListItem key={index}>
                                                    <Box
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: '50%',
                                                            bgcolor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : 'grey.300',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            mr: 1.5,
                                                            fontSize: 13,
                                                            fontWeight: 'bold',
                                                            color: index < 3 ? (index === 0 ? '#000' : '#fff') : 'text.secondary',
                                                        }}
                                                    >
                                                        {index + 1}
                                                    </Box>
                                                    <ListItemText
                                                        primary={member.jmeno}
                                                        secondary={`Ø ${formatujDobuReakce(member.prumernaDobaReakce)}`}
                                                    />
                                                    {member.funkce && (
                                                        <Chip label={member.funkce} size="small" variant="outlined" />
                                                    )}
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Grid>
                                )}
                            </Grid>
                        </>
                    )}
                </Paper>
            )}

            {/* Dostupnost + Pohotovost */}
            <Grid container spacing={2.5} sx={{ mb: 3, alignItems: 'stretch' }}>
                {/* Dostupnost jednotky */}
                {dostupnostStats && (
                    <Grid size={{ xs: 12, md: aktivniPohotovosti && aktivniPohotovosti.length > 0 ? 6 : 12 }} sx={{ display: 'flex' }}>
                        <Paper sx={{ p: 2.5, flex: 1 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <EventAvailableIcon color="primary" />
                                    <Typography variant="h6" fontWeight={600}>Dostupnost jednotky</Typography>
                                </Box>
                                <Button
                                    component={RouterLink}
                                    to="/admin/dostupnost"
                                    size="small"
                                    endIcon={<OpenInNewIcon />}
                                >
                                    Detail
                                </Button>
                            </Box>
                            {currentShift && (
                                <Alert severity="info" sx={{ mb: 1.5, py: 0 }}>
                                    Dnes slouží <strong>směna {currentShift}</strong>
                                </Alert>
                            )}
                            <Tabs
                                value={expandedDostupnost || false}
                                onChange={(_, v) => setExpandedDostupnost(v)}
                                variant="fullWidth"
                                sx={{ minHeight: 36, mb: 1 }}
                                TabIndicatorProps={{ sx: { display: 'none' } }}
                            >
                                <Tab
                                    value="dostupni"
                                    label={`${dostupnostStats.dostupni.length} dostupných`}
                                    sx={{
                                        minHeight: 36, py: 0.5, fontSize: '0.8125rem', fontWeight: 600,
                                        color: 'success.main',
                                        bgcolor: expandedDostupnost === 'dostupni' ? 'success.main' : 'transparent',
                                        '&.Mui-selected': { color: 'success.contrastText', bgcolor: 'success.main' },
                                        borderRadius: 1,
                                    }}
                                />
                                <Tab
                                    value="omezene"
                                    label={`${dostupnostStats.omezene.length} omezeně`}
                                    sx={{
                                        minHeight: 36, py: 0.5, fontSize: '0.8125rem', fontWeight: 600,
                                        color: 'warning.main',
                                        bgcolor: expandedDostupnost === 'omezene' ? 'warning.main' : 'transparent',
                                        '&.Mui-selected': { color: 'warning.contrastText', bgcolor: 'warning.main' },
                                        borderRadius: 1,
                                    }}
                                />
                                <Tab
                                    value="nedostupni"
                                    label={`${dostupnostStats.nedostupni.length} nedostupných`}
                                    sx={{
                                        minHeight: 36, py: 0.5, fontSize: '0.8125rem', fontWeight: 600,
                                        color: 'error.main',
                                        bgcolor: expandedDostupnost === 'nedostupni' ? 'error.main' : 'transparent',
                                        '&.Mui-selected': { color: 'error.contrastText', bgcolor: 'error.main' },
                                        borderRadius: 1,
                                    }}
                                />
                                {currentShift && dostupnostStats.naSmene.length > 0 && (
                                    <Tab
                                        value="naSmene"
                                        label={`${dostupnostStats.naSmene.length} na směně`}
                                        sx={{
                                            minHeight: 36, py: 0.5, fontSize: '0.8125rem', fontWeight: 600,
                                            color: 'info.main',
                                            bgcolor: expandedDostupnost === 'naSmene' ? 'info.main' : 'transparent',
                                            '&.Mui-selected': { color: 'info.contrastText', bgcolor: 'info.main' },
                                            borderRadius: 1,
                                        }}
                                    />
                                )}
                            </Tabs>
                            {expandedDostupnost && dostupnostStats[expandedDostupnost].length > 0 && (
                                <Table size="small">
                                    <TableBody>
                                        {dostupnostStats[expandedDostupnost].map((d) => (
                                            <TableRow key={d.id}>
                                                <TableCell sx={{ py: 0.5, pl: 0 }}>{d.jmeno}</TableCell>
                                                <TableCell sx={{ py: 0.5 }}>{typeof d.funkce === 'string' ? d.funkce : Array.isArray(d.funkce) ? (d.funkce as any[]).map(f => typeof f === 'string' ? f : f?.kod).filter(Boolean).join(', ') : (d.funkce as any)?.kod || ''}</TableCell>
                                                <TableCell sx={{ py: 0.5 }}>{typeof d.specializace === 'string' ? d.specializace : Array.isArray(d.specializace) ? (d.specializace as any[]).map(s => typeof s === 'string' ? s : s?.kod).filter(Boolean).join(', ') : (d.specializace as any)?.kod || ''}</TableCell>
                                                {currentShift && (
                                                    <TableCell sx={{ py: 0.5 }} align="right">
                                                        {d.hzsSmena && (
                                                            <Chip
                                                                label={d.hzsSmena}
                                                                size="small"
                                                                color={currentShift === d.hzsSmena ? 'error' : 'info'}
                                                                variant={currentShift === d.hzsSmena ? 'filled' : 'outlined'}
                                                            />
                                                        )}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                            {expandedDostupnost && dostupnostStats[expandedDostupnost].length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                                    Žádní členové
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                )}

                {/* Aktivní pohotovost */}
                {aktivniPohotovosti && aktivniPohotovosti.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                        <Paper sx={{ p: 2.5, flex: 1 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <WarningAmberIcon color="warning" />
                                    <Typography variant="h6" fontWeight={600}>Aktivní pohotovost</Typography>
                                </Box>
                                <Button
                                    size="small"
                                    endIcon={<OpenInNewIcon />}
                                    onClick={() => navigate('/admin/pohotovost')}
                                >
                                    Detail
                                </Button>
                            </Box>
                            {aktivniPohotovosti.map(p => (
                                <PohotovostDashboardItem key={p.id} pohotovost={p} />
                            ))}
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {/* Kontroly a povinnosti - Paper sekce */}
            {hasKontrolyProblems && (
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12 }}>
                        <Paper sx={{ p: 2.5 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <FactCheckIcon color="primary" />
                                    <Typography variant="h6" fontWeight={600}>Kontroly a povinnosti</Typography>
                                    {(kontrolyExpired.length > 0 || technikaKontrolyExpired.length > 0) && (
                                        <Chip color="error" label={`${kontrolyExpired.length + technikaKontrolyExpired.length} expirovaných`} size="small" />
                                    )}
                                    {(kontrolyApproaching.length > 0 || technikaKontrolyApproaching.length > 0) && (
                                        <Chip color="warning" label={`${kontrolyApproaching.length + technikaKontrolyApproaching.length} blížících se`} size="small" />
                                    )}
                                    {kontrolyMissing > 0 && (
                                        <Chip color="info" label={`${kontrolyMissing}× chybí`} size="small" />
                                    )}
                                </Box>
                                <Button
                                    component={RouterLink}
                                    to="/admin/kontroly"
                                    size="small"
                                    endIcon={<OpenInNewIcon />}
                                >
                                    Kontroly
                                </Button>
                            </Box>
                            <List dense disablePadding>
                                {/* Expirované kontroly osob */}
                                {kontrolyExpired.map((k, i) => (
                                    <ListItem key={`k-exp-${i}`} disableGutters sx={{ py: 0.25, px: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                                        <ListItemText
                                            primary={`${k.uzivatelJmeno} — ${k.typNazev}`}
                                            secondary={k.datumExpirace ? `expirováno ${dayjs(k.datumExpirace).format('D.M.YYYY')}` : 'expirováno'}
                                        />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip label="Expirováno" color="error" size="small" />
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setKontrolaDialogState({
                                                    open: true,
                                                    uzivatelId: k.uzivatelId,
                                                    uzivatelJmeno: k.uzivatelJmeno,
                                                    kontrolaStatus: k.kontrolaStatus,
                                                })}
                                            >
                                                Zaznamenat
                                            </Button>
                                        </Box>
                                    </ListItem>
                                ))}
                                {/* Expirované kontroly techniky */}
                                {technikaKontrolyExpired.map((k, i) => (
                                    <ListItem key={`t-exp-${i}`} disableGutters sx={{ py: 0.25, px: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                                        <ListItemText
                                            primary={`${k.volaciZnak} — ${k.typNazev}`}
                                            secondary={k.datumExpirace ? `expirováno ${dayjs(k.datumExpirace).format('D.M.YYYY')}` : 'expirováno'}
                                        />
                                        <Chip label="Expirováno" color="error" size="small" />
                                    </ListItem>
                                ))}
                                {/* Blížící se kontroly osob */}
                                {kontrolyApproaching.map((k, i) => (
                                    <ListItem key={`k-app-${i}`} disableGutters sx={{ py: 0.25, px: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                                        <ListItemText
                                            primary={`${k.uzivatelJmeno} — ${k.typNazev}`}
                                            secondary={k.datumExpirace ? `do ${dayjs(k.datumExpirace).format('D.M.YYYY')}` : 'blíží se'}
                                        />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip label="Blíží se" color="warning" size="small" />
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setKontrolaDialogState({
                                                    open: true,
                                                    uzivatelId: k.uzivatelId,
                                                    uzivatelJmeno: k.uzivatelJmeno,
                                                    kontrolaStatus: k.kontrolaStatus,
                                                })}
                                            >
                                                Zaznamenat
                                            </Button>
                                        </Box>
                                    </ListItem>
                                ))}
                                {/* Blížící se kontroly techniky */}
                                {technikaKontrolyApproaching.map((k, i) => (
                                    <ListItem key={`t-app-${i}`} disableGutters sx={{ py: 0.25, px: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                                        <ListItemText
                                            primary={`${k.volaciZnak} — ${k.typNazev}`}
                                            secondary={k.datumExpirace ? `do ${dayjs(k.datumExpirace).format('D.M.YYYY')}` : 'blíží se'}
                                        />
                                        <Chip label="Blíží se" color="warning" size="small" />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Kontrola Dialog */}
            <KontrolaDialog
                open={kontrolaDialogState.open}
                onClose={() => setKontrolaDialogState(prev => ({ ...prev, open: false }))}
                uzivatelId={kontrolaDialogState.uzivatelId}
                uzivatelJmeno={kontrolaDialogState.uzivatelJmeno}
                kontrolaStatus={kontrolaDialogState.kontrolaStatus}
            />

            {/* Charts + System Health - Bento Layout */}
            <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2.5 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            Poplachy za posledních 7 dní
                        </Typography>
                        <Box sx={{ width: '100%', height: 250 }}>
                            <ResponsiveContainer>
                                <BarChart data={barChartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.secondary, 0.15)} />
                                    <XAxis dataKey="den" stroke={theme.palette.text.secondary} fontSize={12} />
                                    <YAxis allowDecimals={false} stroke={theme.palette.text.secondary} fontSize={12} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: theme.palette.background.paper,
                                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                            borderRadius: 12,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        }}
                                    />
                                    <Legend />
                                    <Bar dataKey="poplachy" name="Poplachy" fill={theme.palette.error.main} radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="odpovedi" name="Odpovědi" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>
                </Grid>

                {pieChartData.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 2.5 }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Poplachy dle typu
                            </Typography>
                            <Box sx={{ width: '100%', height: 250 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={pieChartData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={80}
                                            paddingAngle={2}
                                            dataKey="value"
                                            label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                                            labelLine={false}
                                        >
                                            {pieChartData.map((_, index) => (
                                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>
                    </Grid>
                )}

                {/* Recent Audit Logs */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2.5 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            Poslední aktivita
                        </Typography>
                        <List dense>
                            {auditLogs?.slice(0, 5).map((log, index) => (
                                <React.Fragment key={log.id}>
                                    <ListItem>
                                        <ListItemText
                                            primary={log.action}
                                            secondary={
                                                <>
                                                    {log.userEmail} - {new Date(log.timestamp).toLocaleString('cs-CZ')}
                                                </>
                                            }
                                        />
                                        <Chip
                                            label={translateStatus(log.status)}
                                            color={log.status === 'SUCCESS' ? 'success' : 'error'}
                                            size="small"
                                        />
                                    </ListItem>
                                    {index < 4 && <Divider />}
                                </React.Fragment>
                            ))}
                            {(!auditLogs || auditLogs.length === 0) && (
                                <ListItem>
                                    <ListItemText secondary="Žádné záznamy" />
                                </ListItem>
                            )}
                        </List>
                    </Paper>
                </Grid>

                {/* Stav systému - na konci */}
                <Grid size={{ xs: 12, md: 6 }}>
                    {SystemHealthSection}
                </Grid>
            </Grid>

            {/* Dialog pro potvrzení deaktivace předpoplachu */}
            <Dialog
                open={predpoplachDeactivateDialogOpen}
                onClose={() => setPredpoplachDeactivateDialogOpen(false)}
            >
                <DialogTitle>Deaktivovat předpoplach?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Opravdu chcete deaktivovat předpoplach "{aktivniPredpoplach?.nazev}"?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPredpoplachDeactivateDialogOpen(false)}>
                        Zrušit
                    </Button>
                    <Button
                        onClick={() => {
                            if (aktivniPredpoplach?.id) {
                                deaktivujPredpoplachMutation.mutate(aktivniPredpoplach.id, {
                                    onSuccess: () => setPredpoplachDeactivateDialogOpen(false),
                                });
                            }
                        }}
                        color="warning"
                        variant="contained"
                        disabled={deaktivujPredpoplachMutation.isPending}
                    >
                        {deaktivujPredpoplachMutation.isPending ? 'Deaktivuji...' : 'Deaktivovat'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminDashboardPage;
